import {
  Button,
  LinearProgress,
  PaperOwnProps,
  Popover,
  Stack,
  StackProps,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import useUppy from "@/features/createProject/uppyFactory/hooks/useUppy";
import { CheckCircleRounded, ErrorRounded } from "@mui/icons-material";
import { ListFilesStatus, ListFilesStatusProps } from "..";
import { UPLOAD_COMPLETE_STATES } from "@/features/createProject";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import { setUploadFilesModal } from "@/reducers/slices/dialogSlice/dialogSlice";
import { PROJECT_FILE_STATUS } from "@/types/project";

export const FileUploadStatus = ({
  fluid = false,
  stackProps,
  showStatusOfSelectedFile = false,
  handleExpand,
  showToggleViewOption = true,
  listFilesStatusProps,
  showListOfFiles = true
}: {
  fluid?: boolean;
  stackProps?: StackProps | PaperOwnProps;
  showStatusOfSelectedFile?: boolean;
  handleExpand?: (val: boolean) => void;
  showToggleViewOption?: boolean;
  listFilesStatusProps?: ListFilesStatusProps;
  showListOfFiles?: boolean;
}): React.ReactNode => {
  const dispatch = useAppDispatch();

  const isShowingDetails = useTypedSelector(
    (state) => state.dialog.uploadFilesModal.isListOfFilesOpen
  );

  const setIsShowingDetails = (val: boolean) => {
    dispatch(
      setUploadFilesModal({
        isListOfFilesOpen: val
      })
    );
  };

  const isListOfFilesOpen = useTypedSelector(
    (state) => state.dialog.uploadFilesModal.isListOfFilesOpen
  );

  const setIsListOfFilesOpen = useCallback(
    (value: boolean) => {
      dispatch(
        setUploadFilesModal({
          isListOfFilesOpen: value
        })
      );
    },
    [dispatch]
  );

  const { videoCount, progress, createProjectPopupFiles, filesData } =
    useUppy();

  const anchorEl = useRef<HTMLElement | null>(null);

  const allFilesAreUploaded = useMemo(() => {
    return !Object.values(
      (showStatusOfSelectedFile ? filesData : createProjectPopupFiles) || {}
    ).some(
      (_: any) => !UPLOAD_COMPLETE_STATES.includes(_.status || _.fileStatus)
    );
  }, [createProjectPopupFiles, showStatusOfSelectedFile, filesData]);

  const failedUploads = useMemo(() => {
    return Object.values(
      (showStatusOfSelectedFile ? filesData : createProjectPopupFiles) || {}
    ).some((_: any) =>
      [PROJECT_FILE_STATUS.FAILED, PROJECT_FILE_STATUS.UNSUPPORTED].includes(
        _.status || _.fileStatus
      )
    );
  }, [createProjectPopupFiles, showStatusOfSelectedFile, filesData]);

  useEffect(() => {
    if (!videoCount && isListOfFilesOpen) {
      setIsListOfFilesOpen(false);
    }
  }, [videoCount, isListOfFilesOpen, setIsListOfFilesOpen]);

  if (!videoCount) {
    return <div></div>;
  }

  return (
    <Stack
      direction={"row"}
      spacing={2}
      justifyContent={"space-between"}
      {...stackProps}
      minWidth={300}
    >
      <Stack flexShrink={0} gap={1} width={fluid ? "auto" : "100%"}>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          {allFilesAreUploaded ? (
            <>
              <Typography variant="subtitle1" fontWeight={500}>
                Uploaded ({videoCount})
              </Typography>
              <CheckCircleRounded
                color={"success"}
                sx={{ height: 20, width: 20 }}
              />
            </>
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight={500}>
                Uploading Files ({videoCount})
              </Typography>
              {failedUploads ? (
                <ErrorRounded color="error" sx={{ height: 20, width: 20 }} />
              ) : null}
            </>
          )}
        </Stack>
        {fluid ? null : (
          <LinearProgress
            value={progress}
            variant="determinate"
            ref={anchorEl}
            color="secondary"
            sx={{
              borderRadius: 4
            }}
          />
        )}
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "14px"
          }}
        >
          {allFilesAreUploaded
            ? "All files are uploaded"
            : "Your files are being uploaded. Please do not close this tab"}
        </Typography>
      </Stack>
      <Stack
        direction={"row"}
        gap={1}
        flexGrow={1}
        justifyContent={"right"}
        maxWidth={"50%"}
        width={"100%"}
      >
        {fluid ? (
          <Stack
            width={"100%"}
            minWidth={300}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <LinearProgress
              value={progress}
              variant="determinate"
              color="secondary"
              sx={{
                width: "100%",
                borderRadius: 4,
                maxWidth: 490
              }}
              ref={anchorEl}
            />
          </Stack>
        ) : null}
        {showToggleViewOption ? (
          <Stack alignItems={"center"} justifyContent={"center"}>
            <Button
              color="secondary"
              onClick={() => {
                if (handleExpand && typeof handleExpand === "function") {
                  handleExpand(!isShowingDetails);
                  return;
                }
                setIsShowingDetails(!isShowingDetails);
              }}
              id="toggle-view-files"
            >
              {isShowingDetails ? "Hide" : "Show"}
            </Button>
          </Stack>
        ) : null}
      </Stack>
      {showListOfFiles ? (
        <Popover
          open={isShowingDetails}
          anchorEl={anchorEl.current}
          anchorOrigin={{
            vertical: -48,
            horizontal: "left"
          }}
          anchorPosition={{
            top: 500,
            left: 800
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          onClose={() => {
            setIsShowingDetails(false);
            if (handleExpand && typeof handleExpand === "function") {
              handleExpand(false);
            }
          }}
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => theme.palette.background.default
              }
            }
          }}
          {...listFilesStatusProps?.popoverProps}
        >
          <ListFilesStatus
            {...{
              anchorEl,
              showStatusOfSelectedFile,
              ...listFilesStatusProps
            }}
          />
        </Popover>
      ) : null}
    </Stack>
  );
};
